import { Period, PeriodMonthJanuary, PeriodTypeYear } from "@dto/period";
import { Item } from "@dto/records";
import { TW_NumberCell, TW_NumberCell_editing } from "@utils/twClasses";
import { RefObject, useEffect, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import { AbstractCell } from "./AbstractCell";
import { CellProps } from "./GridCell";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";


export function PeriodYearCell({ field, item, position, updateValue, isEditing, inputRef, blurTriggered }: CellProps) {
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;

    const intRegex = /^-?\d*$/;

    const [text, setText] = useState<string | null>(null);
    const wsColor = useModelStore(state => state.wsColor);
    const [lastBlurTriggered, setLastBlurTriggered] = useState<number | null>(null);

    // console.log("Rendering NumberCell", posToString(position));
    useEffect(() => {
        if (item?.value) {
            const period = Period.fromObject(item.value);
            setText(period?.year()?.toString() || "");
        } else {
            setText(null);
        }
    }, [item]);

    useEffect(() => {
        if (text && text.length >= 4) {
            const newValue = textToValue(text);
            if (isEditing && lastBlurTriggered && blurTriggered && lastBlurTriggered !== blurTriggered && item?.value !== newValue) {
                updateValue(item, newValue);
            }
            setLastBlurTriggered(blurTriggered);
        }
    }, [blurTriggered]);

    const updateLocalValue = (item: Item, value: any) => {
        setText(value || "");
        updateValue(item, value);
    }

    const textToValue = (text: string | null): any => {
        if (!text || text.length === 0) {
            return null;
        }
        const year = parseInt(text);
        if (isNaN(year)) {
            return null;
        }
        return Period.newPeriod(year, PeriodMonthJanuary, 1, PeriodTypeYear);
    }

    const valueToText = (): string => {
        const period = Period.fromObject(item?.value);
        return period ? period.year().toString() : "";
    }

    function isTextValid(value: string | null) {
        if (!value || value.length === 0) {
            return true;
        }
        return intRegex.test(value) && value.length <= 4;
    }

    const renderReadOnlyContent = () => {
        const valueStr = valueToText();
        return (
            <div className={TW_NumberCell + (readOnly ? " cursor-not-allowed" : "")}>
                {valueStr}
            </div>
        )
    }

    const renderEditingContent = (
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
        onEnterKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void,
        inputRef: RefObject<HTMLInputElement>
    ) => {
        return (
            <input
                className={TW_NumberCell_editing}
                type="text"
                value={text ?? ""}
                onChange={onTextChange}
                onKeyDown={onEnterKeyPress}
                autoFocus={true}
                style={{ borderColor: wsColor }}
                ref={inputRef}
            />
        )
    }

    return <AbstractCell
        item={item}
        position={position}
        updateValue={updateLocalValue}
        renderReadOnlyContent={renderReadOnlyContent}
        renderEditingContent={renderEditingContent}
        textToValue={textToValue}
        isTextValid={isTextValid}
        text={text}
        setText={setText}
        isEditing={isEditing}
        inputRef={inputRef}
        readOnly={readOnly}
    />
}
